import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { EntityLink } from "scmp-app/components/entity-link";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { itemVideoSectionSearchResultListItemVideoSection$key } from "scmp-app/queries/__generated__/itemVideoSectionSearchResultListItemVideoSection.graphql";

import { getVideoSectionTitleFromUrlAlias } from "./helpers";
import { Container, TitleContainer, VideoSectionTitle } from "./styles";

export type Props = {
  className?: string;
  reference: itemVideoSectionSearchResultListItemVideoSection$key;
};

export const VideoSectionSearchResultListItem: FunctionComponent<Props> = ({
  className,
  reference,
}) => {
  const videoSection = useFragment(
    graphql`
      fragment itemVideoSectionSearchResultListItemVideoSection on VideoSection {
        entityId
        name
        urlAlias
        ...entityLink
      }
    `,
    reference,
  );

  const { currentValue, initialSearchId } = useSearchContext();

  const handleVideoSectionClick = useCallback(() => {
    // GA4 21.2.1
    sendGA4Tracking({
      action: "click",
      category: "search",
      customized_parameters: {
        search_id: initialSearchId,
        search_text: currentValue,
        section_id: videoSection.entityId,
      },
      subcategory: "result",
    } as SearchEvent);
  }, [currentValue, initialSearchId, videoSection.entityId]);

  return (
    <Container className={className}>
      <EntityLink onClick={handleVideoSectionClick} reference={videoSection}>
        <TitleContainer>
          <VideoSectionTitle>
            {getVideoSectionTitleFromUrlAlias({
              name: videoSection?.name,
              urlAlias: videoSection?.urlAlias,
            })}
          </VideoSectionTitle>
        </TitleContainer>
      </EntityLink>
    </Container>
  );
};

VideoSectionSearchResultListItem.displayName = "VideoSectionSearchResultListItem";
